import { useEffect, useState } from "react";
import { ReturnStates } from "./ReturnStates";
import NavButtons from "./NavButtons";
import "./ReturnsOrderSelection.css";
import { defaultTheme, useTheme, useFlowPortalContent } from "../../context/PortalThemeContext";
import { fallbackString } from "../shared/FallbackUtils";
import { ReturnStatus } from "./ReturnStatus";
import { getDiscountedPrice } from "../shared/ProductVariantCards";
import { getDiscountInfo } from "../../utils/discountUtils";
import { Form } from "react-bootstrap";
import { PiCheck } from "react-icons/pi";

const ReturnsOrderSelection = ({ orderDetails, itemsToReturn, onStepChange, selectedItem, setSelectedItem, shopifyProducts, flowType = 'RETURN' }) => {
  const [error, setError] = useState(false);
  const theme = useTheme();
  const [returnQuantitys, setReturnQuantitys] = useState(selectedItem ? { [selectedItem.id]: selectedItem.returnQuantity } : {});
  const [quantitiesAlreadyReturned, setQuantitiesAlreadyReturned] = useState({});
  const [isInReturnMap, setIsInReturnMap] = useState({});

  const defaultSettings = defaultTheme.flowPortalContentSettings.RETURN.itemSelection;

  // Use the new flow-specific hook
  const flowPortalContent = useFlowPortalContent(flowType);
  const itemSelectionSettings = flowPortalContent?.itemSelection || defaultSettings;

  // Calculate total order value for proportional discounts
  const subtotalLineItems = orderDetails.line_items.reduce((sum, item) => sum + parseFloat(item.price), 0);

  const handleQuantityChange = (itemId, quantity) => {
    setReturnQuantitys(prev => ({
      ...prev,
      [itemId]: quantity
    }));

    // If changing after selected, update
    if (selectedItem) {
      setSelectedItem({ ...selectedItem, returnQuantity: quantity });
    }
  }

  const updateQuantitiesAlreadyReturned = (itemId, quantity) => {
    setQuantitiesAlreadyReturned(prev => ({
      ...prev,
      [itemId]: quantity
    }));
  }

  useEffect(() => {
    orderDetails.line_items.forEach((lineItem) => {
      let isInCurrentReturn = false;
      const lineItemIdStr = String(lineItem.id);
      let quantityAlreadyInReturns = itemsToReturn.reduce((acc, item) => {
        if (String(item.id) === lineItemIdStr) {
          isInCurrentReturn = true;
          return acc + (item.returnQuantity || item.quantity);
        }
        return acc;
      }, 0);

      const isInPreviousReturn = Array.isArray(orderDetails.orderReturns) &&
        orderDetails.orderReturns.some(ret =>
          ret.returnedItems?.some((returnedItem) => {
            if (String(returnedItem.itemId) === lineItemIdStr) {
              quantityAlreadyInReturns += (returnedItem.quantity || 1);
              return true;
            }
            return false;
          }
          )
        );

      setQuantitiesAlreadyReturned(prev => ({
        ...prev,
        [lineItemIdStr]: quantityAlreadyInReturns
      }));

      setIsInReturnMap(prev => ({
        ...prev,
        [lineItemIdStr]: (isInCurrentReturn || isInPreviousReturn) && quantityAlreadyInReturns === lineItem.quantity
      }));
    });
  }, [orderDetails.line_items, itemsToReturn, orderDetails.orderReturns]);

  const toggleItemSelection = (item) => {
    if (selectedItem?.id === item.id) {
      setSelectedItem(null);
    } else {
      setSelectedItem({ ...item, returnQuantity: (returnQuantitys[item.id] || 1) });
    }

    error && setError(false);
  };

  const handleBack = () => {
    setSelectedItem(null);
    onStepChange(itemsToReturn.length > 0 ? ReturnStates.RETURN_CONFIRMATION : ReturnStates.ORDER_SUMMARY);
  }

  return (
    <div className="returns-order-selection">
      <div className="returns-order-header">
        <h2>{fallbackString(itemSelectionSettings?.headingText, defaultSettings.headingText)}</h2>
        <p>{fallbackString(itemSelectionSettings?.subheadingText, defaultSettings.subheadingText)}</p>
      </div>

      <div className="return-items-list">
        {orderDetails.line_items.map((item, i) => {
          const imageSrc = item.image_src || item.image;
          const optionMap = item.option_map || {};
          const displayName = item.name;
          const inReturn = isInReturnMap[item.id];
          const quantityAlreadyReturned = quantitiesAlreadyReturned[item.id] || 0;
          const availableQuantity = (item.quantity || 0) - quantityAlreadyReturned;
          const currentReturnQuantity = returnQuantitys[item.id] || 1;
          const isEligibleForReturn = item.eligibleForReturn !== false && !inReturn;
          const isSelected = selectedItem?.id === item.id;
          let reasonForIneligibility = null;

          if (!isEligibleForReturn) {
            if (item?.reasonForIneligibility) {
              reasonForIneligibility = item.reasonForIneligibility;
            } else if (inReturn) {
              reasonForIneligibility = "Item already in return";
            } else {
              reasonForIneligibility = "Not available for return";
            }
          }

          if (availableQuantity <= 0) return null;

          // Use discount allocations if available, otherwise fallback to legacy logic
          let hasDiscount = false;
          let discountedPrice = parseFloat(item.price || "0");

          if (item.discount_allocations !== undefined && Array.isArray(item.discount_allocations)) {
            const discountInfo = getDiscountInfo(item);
            hasDiscount = discountInfo.shouldShowCrossedOut;
            discountedPrice = discountInfo.discountedPrice;
          } else {
            discountedPrice = getDiscountedPrice(item, orderDetails.current_total_discounts, subtotalLineItems);
            hasDiscount = parseFloat(orderDetails.current_total_discounts || "0.00") > 0 || parseFloat(item.total_discount || "0.00") > 0;
          }

          const variantText = Object.entries(optionMap).map(([key, value]) => `${key}: ${value}`).join(', ')
            || item.variant_title;

          return (
            <button
              key={i}
              disabled={!isEligibleForReturn}
              className={`return-item-card ${isSelected ? 'selected' : ''} ${!isEligibleForReturn ? 'disabled' : ''}`}
              onClick={() => isEligibleForReturn && toggleItemSelection(item)}
            >
              <div className="return-item-thumb">
                {imageSrc ? (
                  <img src={imageSrc} alt={displayName} />
                ) : (
                  <div className="return-item-thumb-placeholder">IMG</div>
                )}
              </div>

              <div className="return-item-info">
                <p className="return-item-name">{displayName}</p>
                <p className="return-item-price">
                  {hasDiscount && (
                    <span className="return-item-price-old">${item.price}</span>
                  )}
                  ${discountedPrice.toFixed(2)}
                </p>
                {variantText && (
                  <p className="return-item-variant">{variantText}</p>
                )}
                {!isEligibleForReturn && (
                  <p className="return-item-ineligible">{reasonForIneligibility}</p>
                )}
              </div>

              <div className="return-item-end">
                {isEligibleForReturn && (
                  <>
                    <span className="return-item-qty">
                      {availableQuantity > 1 ? (
                        <Form.Select
                          value={currentReturnQuantity}
                          onClick={(e) => e.stopPropagation()}
                          onChange={(e) => handleQuantityChange(item.id, parseInt(e.target.value))}
                          className="return-item-qty-select"
                        >
                          {Array.from({ length: availableQuantity }, (_, i) => i + 1).map((q) => (
                            <option key={q} value={q}>{q}</option>
                          ))}
                        </Form.Select>
                      ) : (
                        `Qty: ${availableQuantity}`
                      )}
                    </span>
                    {isSelected && (
                      <span className="return-item-badge">
                        <PiCheck size={12} /> Selected
                      </span>
                    )}
                  </>
                )}
              </div>
            </button>
          );
        })}
      </div>

      {error && (
        <div className="alert alert-danger">
          Please select at least one item to proceed.
        </div>
      )}

      <NavButtons
        handleBack={handleBack}
        handleNext={() => onStepChange(ReturnStates.REASON_SELECTION)}
        disabled={!selectedItem}
        onDisabledClick={() => setError(true)}
      />
    </div>
  );
};

export default ReturnsOrderSelection;
